import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import {
    Box,
    Button,
    Center,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Icon,
    useToast,
    Spinner,
    Alert,
    AlertIcon,
    Badge
} from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";
import moment from "moment";

export const CourseInterestReceived = ({ registrationId: registrationIdProp } = {}) => {
    // Support being rendered inside RegistrationHub (/register/:id) or the legacy
    // standalone route (/course-interest-received/:registrationID). A prop always wins.
    const params = useParams();
    const registrationID = registrationIdProp || params.id || params.registrationID;
    const navigate = useNavigate();
    const toast = useToast();
    const [registrationData, setRegistrationData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        const fetchRegistration = async () => {
            setLoading(true);
            try {
                // First try to get the registration details
                const { data: registrationData, error: regError } = await supabase
                    .from("registration")
                    .select("*")
                    .eq("id", registrationID)
                    .single();

                if (regError) throw regError;

                // Check if this is a full certification registration
                // Note: registration table may not have full_certification_id directly
                // It might be stored in draft_cert_run_code or accessed via views
                if (registrationData.draft_cert_run_code) {
                    // Get the full certification run and then the certification
                    const { data: certRunData, error: certRunError } = await supabase
                        .from("full_certification_runs")
                        .select(`
                            id,
                            full_certification (
                                code,
                                name
                            )
                        `)
                        .eq("id", registrationData.draft_cert_run_code)
                        .single();
                    
                    if (!certRunError && certRunData?.full_certification) {
                        registrationData.full_certification_name = certRunData.full_certification.name;
                    }
                } else if (registrationData.draft_course_run_code && !registrationData.draft_cert_run_code) {
                    // Standalone course: fetch course_run + course_type + sessions for email and display
                    const { data: courseRun, error: runError } = await supabase
                        .from("course_run")
                        .select("*, course_type(*)")
                        .eq("id", registrationData.draft_course_run_code)
                        .single();
                    if (!runError && courseRun) {
                        registrationData.courseName = courseRun.course_type?.name || courseRun.course_type_code || "Course";
                        registrationData.courseStartDate = courseRun.start_date;
                        registrationData.courseEndDate = courseRun.end_date;
                        registrationData.venue = courseRun.venue || "To be confirmed";
                        registrationData.is_course_run_confirmed = courseRun.is_course_run_confirmed;
                    }
                    const { data: sessions, error: sessError } = await supabase
                        .from("session")
                        .select("session_date, start_time, end_time, custom_venue, venue_id, custom_room, room_id")
                        .eq("course_run_code", registrationData.draft_course_run_code)
                        .order("session_date", { ascending: true });
                    if (!sessError && sessions?.length) {
                        registrationData.sessions = sessions;
                    } else {
                        registrationData.sessions = [];
                    }
                } else if (registrationData.full_certification_id) {
                    // Fallback: if full_certification_id exists (from a view), use code instead of id
                    const { data: certData, error: certError } = await supabase
                        .from("full_certification")
                        .select("name")
                        .eq("code", registrationData.full_certification_id)
                        .single();

                    if (!certError && certData) {
                        registrationData.full_certification_name = certData.name;
                    }
                }

                setRegistrationData(registrationData);
            } catch (error) {
                console.error("Error fetching registration:", error);
                toast({
                    title: "Error",
                    description: "Could not load registration details.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchRegistration();
    }, [registrationID, toast]);

    console.log('registrationData', registrationData);
    // Email for this stage (awaiting_course_confirmation) is sent by Registration.js
    // at submission time, not from this component.

    if (loading) {
        return (
            <Center minH="100vh" bg="gray.50">
                <Spinner size="xl" color="teal.500" />
            </Center>
        );
    }

    if (!registrationData) {
        return (
            <Center minH="100vh" bg="gray.50">
                <Text color="gray.600">Registration not found.</Text>
            </Center>
        );
    }

    console.log('registrationData', registrationData);

    return (
        <Box minH="100vh" bg="gray.50" py={8}>
            <Container maxW="container.md">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Box
                        bg="white"
                        p={8}
                        borderRadius="lg"
                        boxShadow="md"
                        textAlign="center"
                    >
                        <VStack spacing={6}>
                            <Icon
                                as={CheckCircleIcon}
                                w={20}
                                h={20}
                                color="green.500"
                            />
                            
                            <Heading size="md" color="gray.800">
                                Registration Received
                            </Heading>

                            <Alert status="info" borderRadius="md">
                                <AlertIcon />
                                <Text fontSize="md">
                                    {registrationData.is_course_run_confirmed ? 'Thank you for your interest in this course! You will receive an email notification with payment instructions.' : 'Thank you for your interest in this course! Our team will review the course demand and confirm the course run. You will receive an email notification once the course is confirmed with payment instructions.'}
                                </Text>
                            </Alert>

                            <Box
                                p={6}
                                borderWidth="1px"
                                borderRadius="md"
                                borderColor="gray.200"
                                bg="gray.50"
                                w="100%"
                            >
                                <VStack spacing={4} align="stretch">
                                    <HStack justify="space-between">
                                        <Text fontWeight="semibold" color="gray.700">
                                            {registrationData.full_certification_name ? 'Program:' : 'Course:'}
                                        </Text>
                                        <Text color="gray.800">
                                            {registrationData.full_certification_name || registrationData.courseName || registrationData.name}
                                        </Text>
                                    </HStack>

                                    {registrationData.courseStartDate && (
                                        <HStack justify="space-between">
                                            <Text fontWeight="semibold" color="gray.700">
                                                Course Dates:
                                            </Text>
                                            <Text color="gray.800">
                                                {moment(registrationData.courseStartDate).format("DD MMM YYYY")}
                                                {registrationData.courseEndDate && registrationData.courseEndDate !== registrationData.courseStartDate
                                                    ? ` – ${moment(registrationData.courseEndDate).format("DD MMM YYYY")}`
                                                    : ''}
                                            </Text>
                                        </HStack>
                                    )}


                                    <HStack justify="space-between">
                                        <Text fontWeight="semibold" color="gray.700">
                                            Participant:
                                        </Text>
                                        <Text color="gray.800">
                                            {registrationData.participant_name}
                                        </Text>
                                    </HStack>

                                    <HStack justify="space-between">
                                        <Text fontWeight="semibold" color="gray.700">
                                            Email:
                                        </Text>
                                        <Text color="gray.800">
                                            {registrationData.participant_email}
                                        </Text>
                                    </HStack>

                                    <HStack justify="space-between">
                                        <Text fontWeight="semibold" color="gray.700">
                                            Registration Type:
                                        </Text>
                                        <Badge colorScheme="purple" variant="subtle">
                                            {registrationData.registration_type || (registrationData.draft_cert_run_code ? "Full Certification" : "—")}
                                        </Badge>
                                    </HStack>

                                    <HStack justify="space-between">
                                        <Text fontWeight="semibold" color="gray.700">
                                            Submitted On:
                                        </Text>
                                        <Text color="gray.800">
                                            {moment(registrationData.created_at).format("DD MMM YYYY, h:mm A")}
                                        </Text>
                                    </HStack>
                                </VStack>
                            </Box>

                            {registrationData.is_course_run_confirmed && (
                                <Button
                                    colorScheme="teal"
                                    size="lg"
                                    onClick={() => navigate(`/enrollment/${registrationData.id}`)}
                                    w="100%"
                                >
                                    Continue to Payment
                                </Button>
                            )}
                        </VStack>
                    </Box>
                </motion.div>
            </Container>
        </Box>
    );
}; 